/**
 * `<caos-query-editor>` — several queries open at once, each in its own tab, with the engine behind a seam.
 * (Catalog: Developer surfaces › QueryEditor — write and run queries, several open at once as tabs, with the editing engine swappable.)
 *
 * The editing engine is NOT imported here. It is mounted through `code-surface-engine.ts`, so the
 * library ships with a working plain-text editor and an application that wants highlighting and
 * completion installs one without this component changing. That indirection is the clause of the
 * specification this component exists to honour, not an accident of layering.
 *
 * TABS ARE THE POINT, and they are why this is not a textarea with a button. Writing a query is
 * iterative — you keep the one that worked while you try the next, and you come back to it. A
 * single-query editor makes a person keep the previous attempt somewhere else, which in practice
 * means they do not keep it. Each tab holds its own text, its own dirty mark and its own last run.
 *
 * WHAT IT DOES NOT DO: run anything. There is no transport in a component library, and putting one
 * here would make the piece unusable by anybody whose queries go somewhere else. Pressing Run (or
 * Ctrl/Cmd+Enter) raises `caos-query-run` carrying the tab and its text; the application runs it and
 * sets `running` while it does. A component that both composed the request and sent it would be two
 * components, and the second one would not be reusable.
 *
 * THE THREE IN-EDITOR CONTROLS — search, copy, prettify — sit on the editor's right edge, the same
 * three in the same place as the code editor and the REST console, because a developer moving
 * between the three surfaces should not have to find them again. Search is disabled rather than
 * hidden when the mounted engine cannot search: a control that is there and unavailable says the
 * capability exists and this engine lacks it; a control that vanishes says nothing at all.
 *
 * Styling seams: `::part(editor)`, `::part(tabs)`, `::part(tab)`, `::part(toolbar)`,
 * `::part(tool)`, `::part(run)`, `::part(status)`, `::part(editor-surface)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { codeSurfaceEngine, CODE_SURFACE_CSS, type CodeSurfaceHandle } from '../code-surface-engine.js';

/** One query open in the editor. */
export interface CaosQueryTab {
  /** Stable key — it addresses the tab in events and in a link to it. */
  readonly key: string;
  /** What the tab is called. */
  readonly label: string;
  /** The query text. */
  readonly query: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  .editor { display: flex; flex-direction: column; gap: var(--caos-space-2); }

  .tabs {
    display: flex;
    align-items: center;
    gap: var(--caos-space-1);
    flex-wrap: wrap;
  }

  .tab {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 10px;
    cursor: pointer;
  }
  .tab[aria-selected="true"] {
    color: var(--caos-color-text);
    border-color: var(--caos-color-accent);
    background: var(--caos-color-accent-soft);
  }
  .tab:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .tab .dirty { color: var(--caos-color-accent); font-weight: 700; }

  .frame { position: relative; }
  .toolbar {
    position: absolute;
    top: var(--caos-space-2);
    right: var(--caos-space-2);
    display: flex;
    gap: 4px;
    z-index: 1;
  }
  .tool {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: 11.5px;
    padding: 3px 7px;
    cursor: pointer;
  }
  .tool:hover:not(:disabled) { color: var(--caos-color-text); border-color: var(--caos-color-border-strong); }
  .tool:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .tool:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .foot { display: flex; align-items: center; justify-content: space-between; gap: var(--caos-space-3); }

  .status {
    font-family: var(--caos-font);
    font-size: 12px;
    color: var(--caos-color-text-muted);
  }

  .run {
    border: 1px solid var(--caos-color-accent);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-family: var(--caos-font);
    font-size: 13px;
    font-weight: 600;
    padding: 6px 14px;
    cursor: pointer;
  }
  .run:hover:not(:disabled) { background: var(--caos-color-accent-fill-hover); }
  .run:disabled { opacity: var(--caos-state-disabled-opacity); cursor: progress; }
  .run:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  ${CODE_SURFACE_CSS}
`);

export class CaosQueryEditor extends CaosElement {
  static get observedAttributes(): string[] {
    return ['active', 'running', 'language', 'run-label', 'status', 'search-term'];
  }

  private frameEl: HTMLDivElement;
  private tabsEl: HTMLDivElement;
  private statusEl: HTMLParagraphElement;
  private runEl: HTMLButtonElement;
  private searchEl: HTMLButtonElement;
  private copyEl: HTMLButtonElement;
  private prettifyEl: HTMLButtonElement;

  private handle: CodeSurfaceHandle | null = null;
  private engineName = '';
  private tabList: readonly CaosQueryTab[] = [];
  /** Live text per tab key — what is on screen, which is not what arrived until it is saved. */
  private text = new Map<string, string>();
  /** What each tab arrived holding, so "changed" is a comparison rather than a flag somebody sets. */
  private pristine = new Map<string, string>();

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'editor';
    wrap.setAttribute('part', 'editor');

    this.tabsEl = document.createElement('div');
    this.tabsEl.className = 'tabs';
    this.tabsEl.setAttribute('part', 'tabs');
    this.tabsEl.setAttribute('role', 'tablist');
    this.tabsEl.setAttribute('aria-label', 'Open queries');

    this.frameEl = document.createElement('div');
    this.frameEl.className = 'frame';

    const toolbar = document.createElement('div');
    toolbar.className = 'toolbar';
    toolbar.setAttribute('part', 'toolbar');
    this.searchEl = this.tool('Search', () => this.search(this.getAttribute('search-term') ?? ''));
    this.copyEl = this.tool('Copy', () => this.copy());
    this.prettifyEl = this.tool('Prettify', () => this.prettify());
    toolbar.append(this.searchEl, this.copyEl, this.prettifyEl);
    this.frameEl.append(toolbar);

    const foot = document.createElement('div');
    foot.className = 'foot';
    this.statusEl = document.createElement('p');
    this.statusEl.className = 'status';
    this.statusEl.setAttribute('part', 'status');
    this.statusEl.setAttribute('role', 'status');
    this.runEl = document.createElement('button');
    this.runEl.type = 'button';
    this.runEl.className = 'run';
    this.runEl.setAttribute('part', 'run');
    this.runEl.addEventListener('click', () => this.run());
    foot.append(this.statusEl, this.runEl);

    wrap.append(this.tabsEl, this.frameEl, foot);
    this.root.append(wrap);
  }

  private tool(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tool';
    b.setAttribute('part', 'tool');
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  connectedCallback(): void {
    this.mountEngine();
    this.render();
  }

  disconnectedCallback(): void {
    this.handle?.destroy();
    this.handle = null;
  }

  attributeChangedCallback(name: string): void {
    if (name === 'language' && this.isConnected) {
      // The language is handed to the engine at mount, so changing it remounts. Whatever is on
      // screen is carried across first — a person who switched language has not asked to lose it.
      const carried = this.handle?.getValue();
      this.handle?.destroy();
      this.handle = null;
      const remounted = this.mountEngine();
      if (carried !== undefined) remounted?.setValue(carried);
    }
    this.render();
  }

  private mountEngine(): CodeSurfaceHandle | null {
    if (this.handle) return this.handle;
    const engine = codeSurfaceEngine();
    this.engineName = engine.name;
    this.handle = engine.mount(this.frameEl, {
      value: this.text.get(this.active) ?? '',
      language: this.getAttribute('language') ?? 'sql',
      readOnly: false,
      label: 'Query',
      onChange: (value) => this.onEdit(value),
      onSubmit: () => this.run(),
    });
    return this.handle;
  }

  get tabs(): readonly CaosQueryTab[] {
    return this.tabList;
  }

  set tabs(next: readonly CaosQueryTab[]) {
    this.tabList = Array.isArray(next) ? next : [];
    const keys = new Set(this.tabList.map((t) => t.key));
    for (const key of [...this.text.keys()]) {
      if (!keys.has(key)) {
        this.text.delete(key);
        this.pristine.delete(key);
      }
    }
    for (const tab of this.tabList) {
      // A tab that is already open keeps what is on screen. Re-supplying the same list — which a
      // host does on every render — must not throw away what somebody has typed since.
      if (!this.text.has(tab.key)) this.text.set(tab.key, tab.query);
      this.pristine.set(tab.key, tab.query);
    }
    this.render();
    this.handle?.setValue(this.text.get(this.active) ?? '');
  }

  /** The key of the tab on screen. Falls back to the first tab rather than showing nothing. */
  get active(): string {
    const asked = this.getAttribute('active');
    if (asked !== null && this.tabList.some((t) => t.key === asked)) return asked;
    return this.tabList[0]?.key ?? '';
  }

  set active(key: string) {
    this.setAttribute('active', key);
  }

  /** What is in the editor right now, for the tab on screen. */
  get value(): string {
    return this.handle?.getValue() ?? this.text.get(this.active) ?? '';
  }

  /** The tabs whose text differs from what was handed in. */
  get changed(): readonly string[] {
    return [...this.text.entries()].filter(([k, v]) => this.pristine.get(k) !== v).map(([k]) => k);
  }

  /** Which engine is mounted — the seam, readable, so an application can show what it installed. */
  get engine(): string {
    return this.engineName;
  }

  private get running(): boolean {
    return this.hasAttribute('running');
  }

  private onEdit(value: string): void {
    this.text.set(this.active, value);
    this.renderTabs();
    this.dispatchEvent(
      new CustomEvent('caos-query-change', {
        bubbles: true,
        composed: true,
        detail: { key: this.active, query: value },
      }),
    );
  }

  private select(key: string): void {
    if (key === this.active) return;
    // What is on screen belongs to the tab being left, and the engine is about to be told to hold
    // something else. Take it back first or it is gone.
    this.text.set(this.active, this.value);
    this.setAttribute('active', key);
    this.handle?.setValue(this.text.get(key) ?? '');
    this.dispatchEvent(new CustomEvent('caos-query-tab', { bubbles: true, composed: true, detail: { key } }));
  }

  private run(): void {
    if (this.running || this.active === '') return;
    this.dispatchEvent(
      new CustomEvent('caos-query-run', {
        bubbles: true,
        composed: true,
        detail: { key: this.active, query: this.value },
      }),
    );
  }

  /**
   * Find `term` in the query and reveal it, returning how many times it occurs.
   *
   * Returns 0 when the mounted engine cannot search, which is the same answer it gives for "not
   * found" — and that is precisely why the toolbar button is DISABLED in that case rather than left
   * to hand back a zero somebody would read as "your text is not in there".
   */
  search(term: string): number {
    const matches = this.handle?.search?.(term) ?? 0;
    this.dispatchEvent(
      new CustomEvent('caos-query-search', { bubbles: true, composed: true, detail: { term, matches } }),
    );
    return matches;
  }

  private copy(): void {
    // Asking for the clipboard, not reaching for it: a component library cannot know whether this
    // page is allowed to write to the clipboard, and a silent rejection is worse than an event.
    this.dispatchEvent(new CustomEvent('caos-query-copy', { bubbles: true, composed: true, detail: { text: this.value } }));
  }

  private prettify(): void {
    // Formatting a query is language knowledge, and this component has none — it only knows which
    // language it was told. The application formats and sets the text back.
    this.dispatchEvent(
      new CustomEvent('caos-query-prettify', {
        bubbles: true,
        composed: true,
        detail: { key: this.active, query: this.value, language: this.getAttribute('language') ?? 'sql' },
      }),
    );
  }

  /** Put text into a tab from outside — what a host calls after prettifying or loading a saved query. */
  setQuery(key: string, query: string): void {
    if (!this.tabList.some((t) => t.key === key)) return;
    this.text.set(key, query);
    if (key === this.active) this.handle?.setValue(query);
    this.renderTabs();
  }

  private render(): void {
    this.renderTabs();
    this.runEl.textContent = this.running ? 'Running…' : (this.getAttribute('run-label') ?? 'Run');
    this.runEl.disabled = this.running || this.tabList.length === 0;
    this.statusEl.textContent = this.getAttribute('status') ?? '';
    this.searchEl.disabled = typeof this.handle?.search !== 'function';
    this.searchEl.title =
      typeof this.handle?.search === 'function' ? 'Search the query' : `The ${this.engineName} editor cannot search`;
  }

  private renderTabs(): void {
    this.tabsEl.replaceChildren(
      ...this.tabList.map((tab) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tab';
        b.setAttribute('part', 'tab');
        b.setAttribute('role', 'tab');
        const selected = tab.key === this.active;
        b.setAttribute('aria-selected', selected ? 'true' : 'false');
        b.tabIndex = selected ? 0 : -1;
        const dirty = this.pristine.get(tab.key) !== this.text.get(tab.key);
        b.append(document.createTextNode(tab.label));
        if (dirty) {
          const dot = document.createElement('span');
          dot.className = 'dirty';
          dot.textContent = '•';
          // Unsaved work is marked in text as well as in the dot, because a bullet is not a word.
          dot.setAttribute('aria-label', 'unsaved changes');
          b.append(dot);
        }
        b.addEventListener('click', () => this.select(tab.key));
        return b;
      }),
    );
  }
}

define('caos-query-editor', CaosQueryEditor);

declare global {
  interface HTMLElementTagNameMap {
    'caos-query-editor': CaosQueryEditor;
  }
}
